import type {
  IModelProperty,
  IModelPropertySet,
} from '@fulcrum/cdm-components';
import {
  Entity,
  IdentifierType,
  OwnerHistoryChangeAction,
  OwnerHistoryState,
  Product,
  UUID,
} from '@technology/web3d-plugin-trimbim';

type EnumToStringMap = Array<[number, string]>;

export interface IEntityInfoLike {
  entity?: Entity;
  product?: Product;
}

export interface IFormatterDependencies {
  stringPropertyType: IModelProperty['type'];
  dateTimePropertyType: IModelProperty['type'];
  convertModelValueToPanelValue(
    value: string | number | bigint | undefined
  ): IModelProperty['value'];
  getEnumName(value: number, map: EnumToStringMap): string;
}

export interface IExtractArtificialPropsParams extends IFormatterDependencies {
  entityInfo: IEntityInfoLike;
  persistentId: string;
  modelName: string;
  layerNames: string[];
}

export interface IExtractProductPropsParams extends IFormatterDependencies {
  product: Product;
}

export function extractArtificialProps(
  params: IExtractArtificialPropsParams
): IModelPropertySet[] {
  const {
    entityInfo,
    persistentId,
    modelName,
    layerNames,
    stringPropertyType,
    dateTimePropertyType,
    convertModelValueToPanelValue,
    getEnumName,
  } = params;
  const refObjectProps: IModelProperty[] = [];

  if (entityInfo.entity) {
    if (entityInfo.entity.identifierType === IdentifierType.Guid) {
      const uuid = entityInfo.entity.identifier as UUID;
      refObjectProps.push({
        name: 'GUID (MS)',
        type: stringPropertyType,
        value: uuid.msGuid,
      });
      refObjectProps.push({
        name: 'GUID (IFC)',
        type: stringPropertyType,
        value: uuid.ifcGuid,
      });
    } else {
      refObjectProps.push({
        name: 'ID',
        type: stringPropertyType,
        value: persistentId,
      });
    }

    const { fileFormat, commonType } = extractFormatAndType(
      entityInfo.entity.class ?? ''
    );
    refObjectProps.push({
      name: 'File Format',
      type: stringPropertyType,
      value: fileFormat,
    });
    refObjectProps.push({
      name: 'Common Type',
      type: stringPropertyType,
      value: commonType,
    });
  }

  if (modelName.length > 0) {
    refObjectProps.push({
      name: 'File Name',
      type: stringPropertyType,
      value: modelName,
    });
  }

  const result: IModelPropertySet[] = [];
  result.push({ name: 'ReferenceObject', properties: refObjectProps });

  if (layerNames.length > 0) {
    result.push({
      name: 'PresentationLayers',
      properties: [
        {
          name: 'Layer',
          type: stringPropertyType,
          value: layerNames.join(', '),
        },
      ],
    });
  }

  if (entityInfo.product) {
    result.push(
      extractProductProps({
        product: entityInfo.product,
        stringPropertyType,
        dateTimePropertyType,
        convertModelValueToPanelValue,
        getEnumName,
      })
    );
  }
  return result;
}

export function extractProductProps(
  params: IExtractProductPropsParams
): IModelPropertySet {
  const {
    product,
    stringPropertyType,
    dateTimePropertyType,
    convertModelValueToPanelValue,
    getEnumName,
  } = params;
  const properties: IModelProperty[] = [
    {
      name: 'Product Name',
      type: stringPropertyType,
      value: product.name ?? '',
    },
    {
      name: 'Product Description',
      type: stringPropertyType,
      value: product.description ?? '',
    },
    {
      name: 'Product Object Type',
      type: stringPropertyType,
      value: product.objectType ?? '',
    },
    {
      name: 'Owning User',
      type: stringPropertyType,
      value: `${product.personId ?? ''}@${product.organizationName ?? ''}`,
    },
    {
      name: 'Creation Date',
      type: dateTimePropertyType,
      value: convertModelValueToPanelValue(product.creationDate),
    },
    {
      name: 'Last Modified Date',
      type: dateTimePropertyType,
      value: convertModelValueToPanelValue(product.lastModificationDate),
    },
    {
      name: 'Change Action',
      type: stringPropertyType,
      value: getEnumName(product.changeAction, historyChangeToString),
    },
    {
      name: 'State',
      type: stringPropertyType,
      value: getEnumName(product.state, historyStateToString),
    },
  ];
  const app = `${product.applicationFullName ?? ''} (${product.applicationIdentifier ?? ''} v${product.applicationVersion ?? ''})`;
  properties.push({
    name: 'Application',
    type: stringPropertyType,
    value: app,
  });
  return { name: 'Product', properties };
}

export function extractFormatAndType(entityClass: string): {
  fileFormat: string;
  commonType: string;
} {
  let fileFormat = '';
  let commonType = '';
  const classUpper = entityClass.toUpperCase();
  if (classUpper.startsWith('IFC') || classUpper.startsWith('DGN')) {
    fileFormat = entityClass.substring(0, 3); // eslint-disable-line no-magic-numbers
    commonType = entityClass.substring(3); // eslint-disable-line no-magic-numbers
  } else if (classUpper.startsWith('AC')) {
    fileFormat = 'DWG';
    commonType = entityClass.substring(2); // eslint-disable-line no-magic-numbers
  } else if (classUpper.startsWith('AEC')) {
    fileFormat = 'DWG';
    commonType = entityClass.substring(3); // eslint-disable-line no-magic-numbers
  } else {
    fileFormat = commonType = 'Invalid';
  }
  return { fileFormat, commonType };
}

export const historyChangeToString: Array<[OwnerHistoryChangeAction, string]> =
  [
    [OwnerHistoryChangeAction.Added, 'Added'],
    [OwnerHistoryChangeAction.Deleted, 'Deleted'],
    [OwnerHistoryChangeAction.Modified, 'Modified'],
    [OwnerHistoryChangeAction.NoChange, 'NoChange'],
  ];

export const historyStateToString: Array<[OwnerHistoryState, string]> = [
  [OwnerHistoryState.Locked, 'Locked'],
  [OwnerHistoryState.ReadOnly, 'ReadOnly'],
  [OwnerHistoryState.ReadOnlyLocked, 'ReadOnlyLocked'],
  [OwnerHistoryState.ReadWrite, 'ReadWrite'],
  [OwnerHistoryState.ReadWriteLocked, 'ReadWriteLocked'],
  [OwnerHistoryState.Undefined, 'Undefined'],
];
